import { find } from "lodash";
import { useCallback, useMemo } from "react";
import { isHotkeyPressed } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";

import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import { findJobsForStep, useBuild } from "~/components/build/Show/lib/BuildContext";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { InputJob, isInputJobUnblocked } from "~/components/build/Show/lib/types";

import {
  DisplayMode,
  getRelevantFilterForStep,
  useFilterStore,
} from "~/components/build/Show/lib/useFilterStore";
import { useSteps } from "~/components/build/Show/lib/useSteps";

/** @deprecated Use `~/components/build/Show/routes/list/-components/StepSearch/useStepSearch` instead. */
export function useStepSearch() {
  const navigate = useNavigate();

  const displayMode = useFilterStore((state) => state.displayMode);
  const expandState = useBuildPreferencesStore((state) => state.expandState);

  const { build } = useBuild();
  const { steps } = useSteps();

  const filteredSteps = useMemo(
    () =>
      steps.filter((step) => {
        if (step.type === "wait") {
          return null;
        }

        if (step.state === "ignored") {
          return false;
        }

        return true;
      }),
    [steps],
  );

  const selectStep = useCallback(
    (uuid: string) => {
      const step = find(steps, { uuid });

      if (!step) {
        return;
      }

      // Navigate to the step
      navigate(resolveStepUrl(step.uuid, { open: !isHotkeyPressed("shift") }));

      // Open unblock dialog if necessary
      if (step.type === "input" || step.type === "block") {
        const [job] = findJobsForStep<InputJob>(build, step.uuid);

        if (!isInputJobUnblocked(job)) {
          Turbo.visit(job.unblockDialogPath, { frame: "unblock_dialog" });
        }
      }

      // Ensure the step is visible in the sidebar
      if (displayMode === DisplayMode.State) {
        const state = getRelevantFilterForStep(step);

        if (state) {
          expandState(state);
        }
      }

      // Focus the step in the sidebar
      window.requestAnimationFrame(() => {
        const sidebarStep = document.querySelector<HTMLAnchorElement>("[aria-current=step]");

        if (sidebarStep) {
          sidebarStep.focus();
        } else {
          (document.activeElement as HTMLInputElement)?.blur();
        }
      });
    },
    [build, filteredSteps, navigate, expandState, displayMode],
  );

  return { steps: filteredSteps, selectStep };
}
